import React, { ChangeEvent, RefObject, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, CheckCircle2Icon, FileUpIcon, ShieldCheckIcon } from 'lucide-react';
import { CarliChat } from './CarliChat';
import { activePrompts, attentionFor, onboardingPrompts, recommendationFor } from '../data/homeSignals';
export type PortalView = 'carli' | 'inventory' | 'performance' | 'marketing' | 'controls' | 'messages' | 'finances' | 'demand' | 'pipeline';
type QuickStats = {
  received: string;
  sold: string;
  sales: string;
  live: string;
  liveStyles?: string;
  maxOpportunity: string;
  msrp?: string;
  salesDelta?: string | null;
  unitsDelta?: string | null;
  forecastSales?: string | null;
  forecastDetail?: string | null;
};
type HomeDashboardProps = {
  selectedOwner: string;
  quickStats: QuickStats;
  hasInventory: boolean;
  shopifyConnected: boolean;
  onConnectShopify: () => void;
  fileInputRef: RefObject<HTMLInputElement>;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  fileName: string | null;
  onRunAnalysis: () => void;
  onNavigate: (view: PortalView) => void;
  latestReport?: {
    name: string;
    onOpen: () => void;
  };
};
export function HomeDashboard({
  selectedOwner,
  quickStats,
  hasInventory,
  fileInputRef,
  onFileChange,
  fileName,
  onRunAnalysis,
  onNavigate,
  latestReport
}: HomeDashboardProps) {
  const [analysisStarted, setAnalysisStarted] = useState(false);
  const attention = hasInventory ? attentionFor(selectedOwner) : [];
  const recommendation = hasInventory ? recommendationFor(selectedOwner) : null;
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" data-id="element-1259" data-mp="14na83nv9n5f0">
      {!hasInventory && <section className="pb-8" aria-labelledby="home-heading" data-id="element-1260" data-mp="zopgudxzrw2p">
          <CarliLabel data-id="element-1261" data-mp="zqjt73v76fv6" />
          <h1 id="home-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-1262" data-mp="3pdri5alnmmxg">Welcome to Carli</h1>
          <p className="mt-3 max-w-2xl text-lg leading-relaxed text-ink/65" data-id="element-1263" data-mp="3keeski3ef1r6">
            Start by adding inventory. Carli will evaluate the opportunity and recommend the best recovery path.
          </p>
        </section>}

      {hasInventory && <section aria-labelledby="snapshot-heading" data-id="element-1264" data-mp="3qskf8cfni932">
          <div className="flex items-center gap-3" data-id="element-1265" data-mp="3cudp5igy1ddy">
            <h2 id="snapshot-heading" className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-1266" data-mp="3hu8ehhl2luww">Your recovery snapshot</h2>
            <span className="h-px flex-1 bg-[#BB5E40]/25" aria-hidden="true" data-id="element-1267" data-mp="334ay3ntdu9i9" />
            {latestReport && <button type="button" onClick={latestReport.onOpen} className="inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-forest hover:underline" data-id="element-1268" data-mp="37wu9ce4lksjw">
                Latest report: {latestReport.name}
                <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1269" data-mp="2y46gtvknhaqz" />
              </button>}
          </div>

          <dl className="mt-6 grid grid-cols-2 gap-x-8 gap-y-7 lg:grid-cols-4" data-id="element-1270" data-mp="2p5kyo1j3bxqq">
            <HeadlineStat label="Total sales" value={quickStats.sales} detail={quickStats.salesDelta ?? 'Gross recovery revenue'} data-id="element-1271" data-mp="2p7fckvvy2plb" />
            <HeadlineStat label="Units sold" value={quickStats.sold} detail={quickStats.unitsDelta ?? 'Across all recovery channels'} data-id="element-1272" data-mp="20l7v2j5cf0pi" />
            <HeadlineStat label="Live units" value={quickStats.live} detail={`Across ${quickStats.liveStyles ?? '0'} styles`} data-id="element-1273" data-mp="2fjjtau8d6his" />
            <HeadlineStat label="Est. next 7 day sales" value={quickStats.forecastSales ?? '—'} detail={quickStats.forecastDetail ?? 'Projected recovery sales'} data-id="element-1274" data-mp="2al8juklze913" />
          </dl>
        </section>}

      <section className="mt-10" aria-labelledby="add-inventory-heading" data-id="element-1275" data-mp="2y7f0twdduz3g">
        <div className="flex items-center gap-3" data-id="element-1276" data-mp="2t8u07vu7ydeh">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-1277" data-mp="2ta5cyl5n6vhq">Add inventory</span>
          <span className="h-px flex-1 bg-[#BB5E40]/25" aria-hidden="true" data-id="element-1278" data-mp="37725qs5bmn19" />
        </div>

        <article className="mt-4 rounded-3xl border border-ink/10 bg-paper p-6 shadow-sm sm:p-8" data-id="element-1279" data-mp="339e290pkblxr">
          <h2 id="add-inventory-heading" className="font-display text-2xl font-medium tracking-tight sm:text-3xl" data-id="element-1280" data-mp="10kmcxypcq323">
            {hasInventory ? 'Keep your recovery growing' : 'Send Carli your first inventory'}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink/65" data-id="element-1281" data-mp="1f9si03avenhw">
            {hasInventory ? `Your current inventory is projected to generate ${quickStats.forecastSales ?? 'additional sales'} over the next 7 days. Upload a spreadsheet and Carli estimates recovery, likely channels, and expected timing before you confirm the upload.` : 'Upload a spreadsheet and Carli estimates recovery, likely channels, and expected timing. Review her report before you confirm the upload.'}
          </p>

          <ol className="mt-6 grid gap-3 sm:grid-cols-3" data-id="element-1282" data-mp="2304mt5atk449">
            <UploadStep number="1" title="Choose how to connect" text="Shopify, a CSV upload, or your 3PL." data-id="element-1283" data-mp="231pg6rledh0j" />
            <UploadStep number="2" title="Carli analyzes it" text="She estimates recovery value, channel fit, and expected timing." data-id="element-1284" data-mp="3rizc2qyv8848" />
            <UploadStep number="3" title="Review, then confirm" text="Top findings first. Nothing goes live until you approve it." data-id="element-1285" data-mp="3nigj2tl8kw0g" />
          </ol>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-id="element-1286" data-mp="3iocc4abz0nzd">
            <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={onFileChange} className="sr-only" aria-label="Upload inventory file" data-id="element-1287" data-mp="3dqsvj3rsvz49" />
            <button type="button" onClick={() => {
            setAnalysisStarted(true);
            onRunAnalysis();
          }} className={`inline-flex items-center justify-center gap-2 rounded-full border px-5 py-3.5 text-sm font-semibold transition-colors ${analysisStarted ? 'border-[#BB5E40]/35 bg-paper text-[#94452E] hover:bg-cream' : 'border-[#BB5E40] bg-[#BB5E40] text-cream hover:bg-[#94452E]'}`} data-id="element-1288" data-mp="33to7szib3vlc">
              <FileUpIcon className="h-4 w-4" aria-hidden="true" data-id="element-1289" data-mp="1c4f5v52x3kmw" />
              Add inventory &amp; run Carli analysis
            </button>
          </div>

          {fileName && <div className="mt-4 flex items-center gap-2 rounded-xl border border-forest/20 bg-forest/5 px-4 py-3 text-sm text-ink/70" role="status" data-id="element-1290" data-mp="136cwa154bjo0">
              <CheckCircle2Icon className="h-4 w-4 shrink-0 text-forest" aria-hidden="true" data-id="element-1291" data-mp="1820koebs08sg" />
              <span data-id="element-1292" data-mp="1vs4puq4m4glk"><strong className="font-semibold text-ink" data-id="element-1293" data-mp="1vs4s6ws34soq">{fileName}</strong> is ready to add to your recovery workspace.</span>
            </div>}

          <p className="mt-6 flex items-start gap-2 border-t border-ink/10 pt-5 text-xs leading-relaxed text-ink/50" data-id="element-1294" data-mp="1lv02wo2y0h3p">
            <ShieldCheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-forest" aria-hidden="true" data-id="element-1295" data-mp="1qt1l76sjoce4" />
            Nothing is added to your live inventory or activated without your approval. ERP and 3PL connections are coming next.
          </p>
        </article>
      </section>

      <section className="mt-10" aria-labelledby="meet-carli-heading" data-id="element-1296" data-mp="7uvihrgtn2bw">
        <div className="flex items-center gap-3" data-id="element-1297" data-mp="2xn13t3hz2bp">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-1298" data-mp="2wu2xgiltl56">Your recovery analyst</span>
          <span className="h-px flex-1 bg-[#BB5E40]/25" aria-hidden="true" data-id="element-1299" data-mp="guilghp78g72" />
        </div>
        <div className="mt-4 grid gap-5 lg:grid-cols-[0.8fr_1.2fr] lg:items-stretch" data-id="element-1300" data-mp="bvp58hij2b37">
          <article className="flex flex-col justify-between p-1 sm:p-2" data-id="element-1301" data-mp="3kdv2552yxov7">
            <div data-id="element-1302" data-mp="3gdb6y71ygvbp">
              <CarliPulse data-id="element-1303" data-mp="3gduq1r3cdprb" />
              <h2 id="meet-carli-heading" className="mt-5 font-display text-3xl font-medium tracking-tight sm:text-4xl" data-id="element-1304" data-mp="3ga6xov9mv9e6">Meet Carli</h2>
              <p className="mt-3 text-sm leading-relaxed text-ink/65" data-id="element-1305" data-mp="3pcg2r5m4uak6">
                Carli watches every unit you send, values it against live secondary-market demand, and tells you what to do next. Ask her anything — she answers from your own numbers.
              </p>
            </div>
            <ul className="mt-6 space-y-2.5 text-sm text-ink/70" data-id="element-1306" data-mp="2c2p7ae7wh340">
              <CarliSkill text="Forecasts what your inventory will recover, and how fast" data-id="element-1307" data-mp="2c4jxo81xxnfh" />
              <CarliSkill text="Prices and routes units across approved channels" data-id="element-1308" data-mp="2g5vepp6r10c2" />
              <CarliSkill text="Flags what needs your approval before anything goes live" data-id="element-1309" data-mp="2l1kf1uqlel9f" />
            </ul>
          </article>

          <div className="overflow-hidden rounded-3xl shadow-sm" data-id="element-1310" data-mp="2v11kcb1xmx4v">
            <CarliChat prompts={hasInventory ? activePrompts : onboardingPrompts} subheading="Ask about your inventory, recovery strategy, performance, or what to do next." context={carliContext(selectedOwner, quickStats, hasInventory)} data-id="element-1311" data-mp="2v0johfj6mkxh" />
          </div>
        </div>
      </section>

      {hasInventory ? <>
          <div className="mt-10 flex items-center gap-3" data-id="element-1312" data-mp="3dslyw958d4za">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-1313" data-mp="3iqnkb0q4pgdx">Carli recommends</h2>
            <span className="h-px flex-1 bg-[#BB5E40]/25" aria-hidden="true" data-id="element-1314" data-mp="1730pkoup6rg0" />
          </div>

          <AttentionSection items={attention} liveUnits={quickStats.live} onNavigate={onNavigate} data-id="element-1315" data-mp="124h3mog3tfnn" />

          {recommendation && <section className="mt-4 overflow-hidden rounded-3xl bg-ink text-cream shadow-sm" aria-labelledby="recommendation-heading" data-id="element-1316" data-mp="x65y2pwudrb8">
              <div className="grid gap-6 p-6 sm:p-9 lg:grid-cols-[1.4fr_0.6fr] lg:items-end" data-id="element-1317" data-mp="s84fs778pw0t">
                <div data-id="element-1318" data-mp="1pvuwqwm2xf9o">
                  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-cream/55" data-id="element-1319" data-mp="1kup1os4y5fwz">
                    <CarliGlyph tone="cream" data-id="element-1320" data-mp="1g375ehdvc3ic" />
                    Recommendation
                  </span>
                  <h2 id="recommendation-heading" className="mt-4 max-w-2xl font-display text-3xl font-medium tracking-tight sm:text-4xl" data-id="element-1321" data-mp="1b3uijwmzywfr">
                    {recommendation.title}
                  </h2>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-cream/65" data-id="element-1322" data-mp="23srm0tmjuoog">{recommendation.observation}</p>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-cream/75" data-id="element-1323" data-mp="fq8nii0s1kfo">{recommendation.reasoning}</p>
                </div>
                <div className="lg:text-right" data-id="element-1324" data-mp="po4tkveuxp6x">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-cream/50" data-id="element-1325" data-mp="poxxzpqcg2lw">Expected impact</p>
                  <p className="mt-2 font-display text-3xl font-semibold tracking-tight" data-id="element-1326" data-mp="knqnhiwtgpbj">{recommendation.impact}</p>
                  <button type="button" onClick={() => onNavigate(recommendation.target)} className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#BB5E40] px-5 py-3 text-sm font-semibold text-cream transition-colors hover:bg-[#94452E]" data-id="element-1327" data-mp="ymgjev7wgiyb">
                    {recommendation.action}
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1328" data-mp="1idbsvwm4yit8" />
                  </button>
                </div>
              </div>
            </section>}
        </> : <section className="mt-8" aria-labelledby="getting-started-heading" data-id="element-1329" data-mp="1x00rzxch6tz3">
          <h2 id="getting-started-heading" className="sr-only" data-id="element-1330" data-mp="2102v8axuzvgh">Getting started</h2>
          <ol className="grid gap-4 lg:grid-cols-3" data-id="element-1331" data-mp="1n1usfme55zxe">
            <Step number="1" title="Add inventory" text="Upload a spreadsheet or connect Shopify." data-id="element-1332" data-mp="1n1332tb50yfk" />
            <Step number="2" title="Set your recovery rules" text="Choose approved channels, pricing boundaries, and timing." data-id="element-1333" data-mp="1s2is0wgfrp38" />
            <Step number="3" title="Review Carli’s recommendation" text="Carli will estimate recovery and recommend a recovery strategy." data-id="element-1334" data-mp="1i1zzzeepx2a8" />
          </ol>
        </section>}

    </div>;
}
function AttentionSection({
  items,
  liveUnits,
  onNavigate
}: {
  items: ReturnType<typeof attentionFor>;
  liveUnits: string;
  onNavigate: (view: PortalView) => void;
}) {
  if (items.length === 0) return null;
  return <section className="mt-4" aria-label="Items that need your attention" data-id="element-1335" data-mp="zca6cumoymjw">
      <ul className="space-y-3" data-id="element-1336" data-mp="zdld93bfoaf8">
        {items.map(item => <li key={item.id} data-id="element-1337" data-mp="g8otizdatk61">
            <article className="flex flex-col gap-4 rounded-2xl border border-[#BB5E40]/25 bg-paper p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6" data-id="element-1338" data-mp="g8yol1d7ahil">
              <div data-id="element-1339" data-mp="6i3w3f9d2lop">
                <h3 className="text-base font-semibold" data-id="element-1340" data-mp="6f803wh2q72w">{item.title}</h3>
                <p className="mt-1 max-w-2xl text-sm leading-relaxed text-ink/65" data-id="element-1341" data-mp="1jk9dcmy15va">{item.detail}</p>
                {item.impact && <p className="mt-2 text-sm font-semibold text-forest" data-id="element-1342" data-mp="z588w6ep44b1">{item.impact}</p>}
              </div>
              <button type="button" onClick={() => onNavigate(item.target)} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-[#BB5E40] px-4 py-2.5 text-sm font-semibold text-cream transition-colors hover:bg-[#94452E]" data-id="element-1343" data-mp="u7oloh00sh6y">
                {item.action}
              </button>
            </article>
          </li>)}
      </ul>
    </section>;
}
function HeadlineStat({
  label,
  value,
  detail
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return <div data-id="element-1344" data-mp="1pf8owtft1fas">
      <dt className="text-xs font-semibold uppercase tracking-wider text-ink/45" data-id="element-1345" data-mp="1ph1v67mi9xi9">{label}</dt>
      <dd data-id="element-1346" data-mp="1ue22elv3d6ay">
        <p className="mt-2 font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl" data-id="element-1347" data-mp="1uebhvfiifv2e">{value}</p>
        <p className="mt-1.5 text-[13px] text-ink/55" data-id="element-1348" data-mp="1z7ebtor796j4">{detail}</p>
      </dd>
    </div>;
}
function Step({
  number,
  title,
  text
}: {
  number: string;
  title: string;
  text: string;
}) {
  return <li className="rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" data-id="element-1349" data-mp="2856xrbrffcfh">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#BB5E40]/12 font-display text-sm font-semibold text-[#94452E]" data-id="element-1350" data-mp="2d5bdv0ofa556">{number}</span>
      <h3 className="mt-4 text-base font-semibold" data-id="element-1351" data-mp="2i2bhz61poxtn">{title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-ink/65" data-id="element-1352" data-mp="isv6fwjw4kyq">{text}</p>
    </li>;
}
function UploadStep({
  number,
  title,
  text
}: {
  number: string;
  title: string;
  text: string;
}) {
  return <li className="rounded-2xl border border-ink/10 bg-cream/40 p-4" data-id="element-1353" data-mp="2e6gtsms5pno0">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#BB5E40] text-xs font-semibold text-cream" data-id="element-1354" data-mp="299718y9h0vvy">{number}</span>
      <p className="mt-3 text-sm font-semibold" data-id="element-1355" data-mp="2n7wycdwaivkb">{title}</p>
      <p className="mt-1 text-xs leading-relaxed text-ink/60" data-id="element-1356" data-mp="2j6bjwo43y980">{text}</p>
    </li>;
}
function CarliPulse() {
  const reduceMotion = useReducedMotion();
  return <div className="relative flex h-24 w-24 items-center justify-center" aria-hidden="true" data-id="element-1357" data-mp="1vewg0qwonjun">
      {!reduceMotion && [0, 1].map(ring => <motion.span key={ring} className="absolute inset-0 rounded-full border border-[#BB5E40]/35" initial={{
      scale: 0.7,
      opacity: 0.5
    }} animate={{
      scale: 1.15,
      opacity: 0
    }} transition={{
      duration: 2.6,
      repeat: Infinity,
      delay: ring * 1.3,
      ease: 'easeOut'
    }} data-id="element-1358" data-mp="249uai2fhb4o3" />)}
      <span className="relative flex h-16 w-16 items-center justify-center rounded-full border border-[#BB5E40]/25 bg-paper" data-id="element-1359" data-mp="17r6g1gs8q4mt">
        <span className="h-9 w-9 bg-[#BB5E40]" style={{
        WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
        maskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskSize: 'contain'
      }} data-id="element-1360" data-mp="xlws9j42frza" />
      </span>
    </div>;
}
function CarliSkill({
  text
}: {
  text: string;
}) {
  return <li className="flex items-start gap-2.5" data-id="element-1361" data-mp="1bed0cybtqg72">
      <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#BB5E40]" aria-hidden="true" data-id="element-1362" data-mp="1bh8xwljrf2uz" />
      <span className="leading-relaxed" data-id="element-1363" data-mp="3r8eq0vy1p8u9">{text}</span>
    </li>;
}
function CarliLabel() {
  return <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-1364" data-mp="3w3aj7zf0q28y">
      <CarliGlyph data-id="element-1365" data-mp="1xnl5knoexe8f" />
      Carli
    </span>;
}
function CarliGlyph({
  tone = 'coral'
}: {
  tone?: 'coral' | 'cream';
}) {
  return <span aria-hidden="true" className={`h-4 w-4 shrink-0 ${tone === 'cream' ? 'bg-cream' : 'bg-[#BB5E40]'}`} style={{
    WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
    maskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    WebkitMaskSize: 'contain',
    maskSize: 'contain'
  }} data-id="element-1366" data-mp="1sw33txekf9me" />;
}
function carliContext(brand: string, stats: QuickStats, hasInventory: boolean) {
  if (!hasInventory) {
    return 'The brand is signed into the Manymoons recovery portal but has not added inventory yet. Help them understand what to send first, what data Carli needs, how recovery pricing works, and which channels might fit. Keep answers to two short paragraphs at most.';
  }
  return [`The brand is ${brand || 'a Manymoons brand'}, signed into their recovery portal.`, `Current position: ${stats.live} active units, ${stats.sold} units sold, ${stats.sales} recovery generated, ${stats.maxOpportunity} of remaining opportunity across ${stats.received} units received.`, 'Answer as their recovery analyst using these numbers. Be specific and commercial. Two short paragraphs at most.'].join(' ');
}